"use client";

import { twMerge } from "tailwind-merge";

import { FaqContainer } from "@/components/faq/FaqContainer";
import { FaqItem } from "@/components/faq/FaqItem";
import { LinkButton } from "@/components/links/LinkButton";
import { TextLink } from "@/components/links/TextLink";
import { Heading2 } from "@/components/text/Heading2";
import { useConversionGuideModal } from "@/lib/zustand/useConversionGuideModal";

export function LandingFaq({ className }: { className?: string }) {
  const setConversionGuideModalOpen = useConversionGuideModal(
    (state) => state.setOpen
  );

  return (
    <div className={twMerge("mx-auto max-w-7xl px-4 lg:px-8", className)}>
      <Heading2 className="mb-16 text-center">
        Frequently asked questions
      </Heading2>
      <FaqContainer>
        <FaqItem question="What exactly is Audimee?">
          With Audimee, musicians can convert vocals to any of our royalty-free
          voices. The generated vocals are copyright free, and can be integrated
          into your own music.
        </FaqItem>

        <FaqItem question="How can I get started with using Audimee?">
          Navigate to the{" "}
          <TextLink href="/create" className="text-base" external={false}>
            Create
          </TextLink>{" "}
          page where you have the option to upload a audio file, record, or
          select a song to cover. Choose a royalty-free voice by listening to
          the previews available and select one that works with your input data.
          Adjust the settings as needed. Once everything is set to your liking,
          press the &apos;Convert&apos; button.
        </FaqItem>

        <FaqItem question="Can I convert vocals in languages other than English?">
          Our royalty-free voices are primarily trained on English singing data,
          so they might encounter difficulties with languages very dissimilar to
          English. The easiest way to find out is to sign up (we offer a free
          plan) and test a few conversions with vocals in the desired language.
          Once subscribed, you also have the option to train your own custom
          voice in any language of your choice.
        </FaqItem>

        <FaqItem question="What rights do I have concerning the vocals generated through Audimee?">
          On a paid plan, you can use the generated vocals for commercial
          purposes, including releasing your tracks on platforms like Spotify.
          You do not need to credit Audimee or the royalty-free voice.
        </FaqItem>

        <FaqItem question="Why doesn't the output meet the expected standard?">
          Although our AI voice conversion technology is robust, there are
          several brief suggestions you can follow to maximize the quality of
          your outcomes. Check out our{" "}
          <LinkButton
            onClick={setConversionGuideModalOpen}
            className="text-base"
          >
            conversion guide
          </LinkButton>
          .
        </FaqItem>

        <FaqItem question="I have run out of conversion time. What are my options?">
          If you&apos;re on the free plan, your conversion minutes won&apos;t
          reset at the end of the month. To get more conversion time,
          you&apos;ll need to{" "}
          <TextLink href="/pricing" className="text-base" external={false}>
            upgrade
          </TextLink>{" "}
          to a paid plan. On the paid plans, your conversion time will refill
          each month.
        </FaqItem>
      </FaqContainer>
    </div>
  );
}
